import { SEND_BATCH, type Broadcast, type NextBatchItem, type ReportItem } from "@tg/shared";
import { api } from "../lib/api";
import { useJobStore } from "../store/useJobStore";
import { engine } from "./MTProtoEngine";
import { processTarget, type SendContext } from "../tg/sender";
import { jitterDelayMs, LongPauseTracker, sleep } from "../tg/rateGovernor";

const MAX_RETRIES = 3;

class SendController {
  private abort: AbortController | null = null;
  private running = false;

  get isRunning(): boolean {
    return this.running;
  }

  async start(jobId: number): Promise<void> {
    if (this.running) return;
    this.running = true;
    this.abort = new AbortController();
    const signal = this.abort.signal;
    const store = useJobStore.getState();

    try {
      store.setJobId(jobId);
      await api.startBroadcast(jobId);
      const job = await api.getBroadcast(jobId);
      store.applyProgress(await api.progress(jobId));
      store.setStatus("running");

      const mediaBlobs = await Promise.all(job.media.map((m) => api.getImageBlob(m.key)));
      const client = await engine.ensureClient();
      const ctx: SendContext = { client, job, mediaBlobs };
      const longPause = new LongPauseTracker(job.pauseEveryN, job.pauseSecs);

      await this.loop(job, ctx, longPause, signal);
    } catch (e) {
      if ((e as Error)?.name === "AbortError") {
        useJobStore.getState().setStatus("paused");
      } else {
        useJobStore.getState().setBanner({ kind: "auth", text: `Error: ${String(e)}` });
        useJobStore.getState().setStatus("paused");
      }
    } finally {
      this.running = false;
      this.abort = null;
      useJobStore.getState().setCurrent(null);
    }
  }

  private async loop(
    job: Broadcast,
    ctx: SendContext,
    longPause: LongPauseTracker,
    signal: AbortSignal,
  ): Promise<void> {
    const store = useJobStore.getState();

    while (!signal.aborted) {
      const nb = await api.nextBatch(job.id, SEND_BATCH);

      if (!nb.items.length) {
        switch (nb.halt) {
          case "completed":
            store.setStatus("completed");
            store.setBanner({ kind: "info" as never, text: "Completado" });
            store.applyProgress(await api.progress(job.id));
            return;
          case "peer_flood":
            store.setStatus("peer_flood_halt");
            store.setBanner({
              kind: "peerflood",
              text: "PEER_FLOOD: cuenta marcada como spam. Envío detenido 24 h.",
              until: nb.retryAt ?? null,
            });
            return;
          case "daily_cap":
            store.setStatus("paused");
            store.setBanner({
              kind: "cap",
              text: "Tope diario alcanzado. Reanudando tras la medianoche UTC.",
              until: nb.retryAt ?? null,
            });
            await this.waitUntil(nb.retryAt, signal);
            continue;
          case "outside_window":
            store.setBanner({
              kind: "window",
              text: "Fuera de la franja horaria. Esperando…",
              until: nb.retryAt ?? null,
            });
            await this.waitUntil(nb.retryAt, signal);
            continue;
          case "scheduled":
            store.setBanner({
              kind: "window",
              text: "Inicio programado pendiente. Esperando…",
              until: nb.retryAt ?? null,
            });
            await this.waitUntil(nb.retryAt, signal);
            continue;
          default:
            // no work claimable yet (leases/defers); brief wait then re-poll
            await sleep(5000, signal);
            continue;
        }
      }

      store.setBanner(null);
      const results: ReportItem[] = [];
      let stopAfter = false;

      for (const t of nb.items) {
        if (signal.aborted) break;
        store.setCurrent({ groupId: t.peerId, title: t.title });

        const outcome = await this.sendWithRetries(ctx, t, signal);
        if (outcome.report) {
          results.push(outcome.report);
          store.pushLog({
            ts: Date.now(),
            title: t.title,
            result: outcome.report.result,
            detail: outcome.report.errorCode ?? outcome.report.variant?.slice(0, 60),
          });
        }
        if (outcome.stop) {
          stopAfter = true;
          if (outcome.stop === "auth") {
            store.setBanner({ kind: "auth", text: "Sesión inválida. Vuelve a iniciar sesión." });
          }
          break;
        }

        const delay = jitterDelayMs(job.minDelayS, job.maxDelayS);
        store.setCurrent({ groupId: t.peerId, title: t.title, nextDelayMs: delay });
        await sleep(delay, signal);

        const lp = longPause.tick();
        if (lp > 0) {
          store.setBanner({ kind: "pause", text: `Pausa larga (${Math.round(lp / 1000)}s)…` });
          await sleep(lp, signal);
          store.setBanner(null);
        }
      }

      if (results.length) {
        const rep = await api.report(job.id, { items: results });
        store.applyProgress(await api.progress(job.id));
        if (rep.peerFloodHalt) {
          store.setStatus("peer_flood_halt");
          store.setBanner({
            kind: "peerflood",
            text: "PEER_FLOOD: cuenta marcada como spam. Envío detenido 24 h.",
            until: rep.cooldownUntil,
          });
          return;
        }
      }
      if (stopAfter) return;
    }
  }

  private async sendWithRetries(ctx: SendContext, t: NextBatchItem, signal: AbortSignal) {
    let outcome = await processTarget(ctx, t);
    let attempts = 0;
    while (outcome.retrySame && attempts < MAX_RETRIES && !signal.aborted) {
      const waitMs = outcome.waitMs ?? 1000;
      useJobStore.getState().setBanner({
        kind: "flood",
        text: `Espera de Telegram: ${Math.round(waitMs / 1000)}s…`,
        until: Date.now() + waitMs,
      });
      await sleep(waitMs, signal);
      attempts++;
      outcome = await processTarget(ctx, t);
    }
    useJobStore.getState().setBanner(null);
    if (outcome.retrySame && !outcome.report) {
      // give up after retries
      return { report: { targetId: t.targetId, result: "failed", errorCode: "RETRY_EXHAUSTED" } as ReportItem };
    }
    return outcome;
  }

  private async waitUntil(retryAt: number | null | undefined, signal: AbortSignal): Promise<void> {
    const ms = retryAt ? Math.max(1000, Math.min(retryAt - Date.now(), 60_000)) : 30_000;
    await sleep(ms, signal);
  }

  async pause(): Promise<void> {
    this.abort?.abort();
    if (useJobStore.getState().jobId) {
      await api.pauseBroadcast(useJobStore.getState().jobId!).catch(() => {});
    }
    useJobStore.getState().setStatus("paused");
  }

  async stop(): Promise<void> {
    this.abort?.abort();
    const id = useJobStore.getState().jobId;
    if (id) await api.stopBroadcast(id).catch(() => {});
    useJobStore.getState().setStatus("stopped");
  }
}

export const sendController = new SendController();
